/**
 * @fileoverview Host the Admin Phone tab: the firm-wide ring time, the caller rules (`CallerRules.js`) and the blocked-caller list (`BlockedCallers.js`). The backend (`kc-internal-backend/functions/phone_settings.py`) owns the ring-time limits and marks every enabled extension for the phone server to pick up. Extensions and direct lines are assigned in People, and each person's hours, Do Not Disturb and voicemail live in Personal Settings. A saved value does not prove the phone server has applied it.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';

import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppIcon, AppSpinner, AppTextInput } from '../shared';
import { AdminCard, AdminNote, AdminSection } from './AdminChrome';
import BlockedCallers from './BlockedCallers';
import CallerRules from './CallerRules';
import './PhoneAdmin.css';


function RingTime() {
  const showToast = useContext(ToastContext);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Until the saved value arrives there is no form, so a failed load can't be saved over the real ring time.
  const load = useCallback(() => {
    let active = true;
    setLoadFailed(false);
    api.get('/admin/phone-settings')
      .then(({ data }) => {
        if (!active) return;
        setSaved(data);
        setDraft(String(data.ring_seconds));
      })
      .catch(() => active && setLoadFailed(true));
    return () => { active = false; };
  }, []);

  useEffect(load, [load]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/phone-settings', { ring_seconds: Number(draft) });
      setSaved(data);
      setDraft(String(data.ring_seconds));
      showToast({ severity: 'success', summary: 'Saved', detail: `Staff phones will ring for ${data.ring_seconds} seconds once the phone server updates.` });
    } catch (error) {
      showToast({ severity: 'error', summary: 'Could not save', detail: error?.response?.data?.error || 'The ring time could not be saved.' });
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) {
    return (
      <AdminCard title="Ring time">
        <AdminNote tone="warning">The ring time could not be loaded, so it can't be changed right now.</AdminNote>
        <div className="phone-admin-actions">
          <AppButton variant="secondary" icon="pi pi-refresh" label="Try again" onClick={load} />
        </div>
      </AdminCard>
    );
  }

  if (saved === null) {
    return <AdminCard title="Ring time"><div className="phone-admin-loading"><AppSpinner /></div></AdminCard>;
  }

  const seconds = Number(draft);
  const valid = Number.isInteger(seconds) && seconds >= saved.min_ring_seconds && seconds <= saved.max_ring_seconds;

  return (
    <AdminCard title="Ring time" description="How long a staff phone rings before an unanswered call moves on, usually to voicemail. It applies to every enabled extension; the intake queue keeps its own timing.">
      <form className="phone-admin-ring" onSubmit={save}>
        <span className="phone-admin-ring__icon" aria-hidden="true"><AppIcon icon="pi pi-clock" /></span>
        <label className="phone-admin-ring__field" htmlFor="phone-ring-seconds">
          <AppTextInput
            id="phone-ring-seconds"
            type="number"
            min={saved.min_ring_seconds}
            max={saved.max_ring_seconds}
            aria-label="Ring time in seconds"
            aria-invalid={!valid}
            value={draft}
            onChange={(event) => setDraft(event.target.value)} />
          <span>seconds, from {saved.min_ring_seconds} to {saved.max_ring_seconds}</span>
        </label>
        <AppButton
          type="submit"
          icon="pi pi-save"
          label={saving ? 'Saving' : 'Save ring time'}
          disabled={saving || !valid || seconds === saved.ring_seconds} />
      </form>
    </AdminCard>
  );
}


function PhoneAdmin() {
  return (
    <AdminSection
      title="Phone"
      description="Firm-wide call settings. Extensions and direct lines are assigned in People, and each person sets their own hours, Do Not Disturb and voicemail in Personal Settings.">
      <div className="phone-admin-grid">
        <RingTime />
        <CallerRules />
        <BlockedCallers />
      </div>
    </AdminSection>
  );
}

export default PhoneAdmin;
